import { lazy } from 'react';
import { createBrowserRouter, redirect } from 'react-router';

import { permissions } from '@/constants/permissions';
import type { AccessKey } from '@/types/permission';

import RootLayout from '@/layouts/root-layout';
import BaseLayout from '@/layouts/base-layout';
import CmsLayout from '@/layouts/cms-layout';
import ConsoleLayout from '@/features/console/layouts/console-layout';

import { AuthService } from '@/features/auth/auth.service';
import type { IVerifyUser } from '@/features/auth/auth.types';
import {
  endViewAsLocally,
  isViewAsExpired,
  profileToUser,
  readViewAs,
  withoutCreate,
} from '@/features/auth/view-as';
import { UserAccessService } from '@/features/user-access/user-access.service';
import { withSelectedCompany } from '@/stores/company.store';

import { combineLoaders } from '@/lib/loader/combine-loader';
import { permissionsLoader } from '@/lib/loader/permission-loader';

import { authRoutes } from '@/features/auth/routes';
import { inventoryRoute } from '@/features/inventory/routes';
import { userAccessRoute } from '@/features/user-access/routes';
import { BookingRoute } from '@/features/booking/routes';
import { scheduleRoute } from '@/features/schedule/routes';
import { availabilityRoute } from '@/features/availability/routes';
import { pricingeRoute } from '@/features/pricing/routes';
import { todayRoute } from '@/features/today/routes';
import { dashboardRoute } from '@/features/dashboard/routes';
import { dashboardV2Route } from '@/features/dashboard-v2/routes';
import { settingCompanyRoute } from '@/features/setting/company/routes';
import { consoleRoute } from '@/features/console/routes';

const PlanboardPage = lazy(() => import('@/features/planboard/pages'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));
const UnauthorizedPage = lazy(() => import('@/pages/unauthorized'));
const TermsOfServicePage = lazy(() => import('@/pages/terms-of-service'));
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy'));
const PaymentSelectPage = lazy(
  () => import('@/features/booking/page/payment-select')
);
const PaymentSuccessPage = lazy(
  () => import('@/features/booking/page/payment-success')
);
const GiftCardSuccessPage = lazy(
  () => import('@/features/pricing/pages/gift-card-success')
);

// Shared auth guard for protected layout groups (CMS + Console). Verifies the
// stored session, redirects to login when absent/invalid, and returns the
// current user plus their resolved permissions.
// While a super Admin views as another role, the profile is the simulated
// user (role, company, locations); verify would answer the Admin's own.
// Undefined once that session is over, so the Admin's own token takes over.
const loadViewAsUser = async (): Promise<IVerifyUser | undefined> => {
  const viewAs = readViewAs();
  if (!viewAs || isViewAsExpired(viewAs.session)) return undefined;

  try {
    const profile = (await UserAccessService.getProfile()).data;
    return profile?.viewAs && profile.role?.name
      ? profileToUser(profile)
      : undefined;
  } catch {
    return undefined;
  }
};

const protectedLoader = async () => {
  const auth = localStorage.getItem('auth');
  if (!auth) return redirect('/');

  if (readViewAs()) {
    const user = await loadViewAsUser();
    if (user) {
      const roleName = user.role?.name as AccessKey;
      return { user, permissions: withoutCreate(permissions[roleName]) };
    }
    endViewAsLocally();
  }

  try {
    const data = await AuthService.verify();

    if (data.data == null) return redirect('/');

    const roleName = data.data.role?.name as AccessKey | undefined;

    const access = roleName && permissions[roleName];

    // Verify answers with the company the account belongs to, which for a
    // super Admin is not the tenant they switched to. Re-apply the selection
    // before the layouts store this user, or every page load would undo it.
    return { user: withSelectedCompany(data.data), permissions: access };
  } catch {
    localStorage.removeItem('auth');
    return redirect('/');
  }
};

const routes = createBrowserRouter([
  {
    // Pathless wrapper around every route group: gives analytics a single
    // mount point with router context, without changing any URL.
    Component: RootLayout,
    children: [
      {
        Component: BaseLayout,
        // ErrorBoundary: ErrorPage,
        loader: async () => {
          const auth = localStorage.getItem('auth');
          if (auth) {
            try {
              const data = await AuthService.verify();

              if (data.data) return redirect('/planner');
            } catch {
              localStorage.removeItem('auth');
            }
          }
        },
        children: [...authRoutes],
      },
      {
        Component: CmsLayout,
        // ErrorBoundary: ErrorPage,
        loader: protectedLoader,
        shouldRevalidate: () => {
          return false;
        },
        children: [
          {
            path: '/planner',
            Component: PlanboardPage,
            loader: combineLoaders(permissionsLoader),
          },
          ...todayRoute,
          ...dashboardRoute,
          ...dashboardV2Route,
          ...BookingRoute,
          ...inventoryRoute,
          ...userAccessRoute,
          ...scheduleRoute,
          ...availabilityRoute,
          ...pricingeRoute,
          ...settingCompanyRoute,
        ],
      },
      {
        Component: ConsoleLayout,
        loader: protectedLoader,
        shouldRevalidate: () => {
          return false;
        },
        children: [...consoleRoute],
      },
      {
        path: '/unauthorized',
        Component: UnauthorizedPage,
      },
      {
        path: '/terms-of-service',
        Component: TermsOfServicePage,
      },
      {
        path: '/privacy-policy',
        Component: PrivacyPolicyPage,
      },
      {
        path: '/payment/select',
        Component: PaymentSelectPage,
      },
      {
        path: '/payment-success',
        Component: PaymentSuccessPage,
      },
      // Public on purpose: the customer who lands here after paying for a gift card
      // has no CMS account, so it sits outside `CmsLayout` and its auth loader.
      {
        path: '/gift-card/success',
        Component: GiftCardSuccessPage,
      },
      {
        path: '*',
        Component: NotFoundPage,
      },
    ],
  },
]);

export default routes;
